import { FC, Props } from "./typings";

import styles from "./styles.module.scss";
import casesService from "api/casesService";
import { ICaseSimple } from "dto/Cases/ICaseSimple";
import { useOnClickOutside } from "hooks/useClickOutside";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDebounce } from "use-debounce";
import Input from "components/Input";
import DropdownInput from "components/DropdownInput";
import SignalsContext from "contexts/SignalsContext";
import { ICase } from "dto/Cases/ICase";
import ModalsManagerContext from "contexts/ModalsManager";
import { useTranslation } from "react-i18next";

const SearchCasesNew: FC<Props> = ({
  className,
  defaultValue,
  isError,
  onChoose,
  settedCase,
  alwaysOpen,
  variant = "filled",
  labelPosition = "top",
  disableBorder = false,
  allowCreateNew = true,
  disableSettingActiveCase = false,
}) => {
  const { t } = useTranslation();
  const tRef = useRef<HTMLInputElement>(null);
  const isCase = window.location.href.search("cases") !== -1;

  const [searchedCaseId, setSearchedCaseId] = useState<null | string>(null);
  const [stopSearchById, setStopSearchById] = useState(false);
  const { modals, setModals } = useContext(ModalsManagerContext);

  const ref = useRef(null);
  const [active, setActive] = useState(alwaysOpen ? true : false);
  const [value, setValue] = useState("");
  const [searchList, setSearchList] = useState<ICaseSimple[]>([]);
  const { parent, setChildren, callback, setCallback } =
    useContext(SignalsContext);

  const [debouncedValue] = useDebounce(value, 300);

  const {
    data: casesData,
    refetch,
    isLoading,
  } = casesService.useGetSimpleCases({
    text: debouncedValue,
    id: searchedCaseId && !debouncedValue ? searchedCaseId : undefined,
  });

  const cases: ICaseSimple[] = useMemo(
    () => casesData?.data ?? [],
    [casesData]
  );

  const outsideRef = useRef(null);

  const [, updateState] = useState<any>();
  const forceUpdate = useCallback(() => updateState({}), []);

  useOnClickOutside(outsideRef, () => {
    if (active && !alwaysOpen) {
      setActive(false);
    }
  });

  useEffect(() => {
    if (!callback) return;
    if (callback.Kind !== 1024) return;
    const c = callback as ICase;
    setValue(c.Title);
    onChoose?.(c);
    setCallback(null);
  }, [callback]);

  useEffect(() => {
    if (searchedCaseId && !stopSearchById) {
      //@ts-ignore
      const c = cases?.Result?.Items[0];
      if (c) {
        setSearchedCaseId(null);
        setValue(c.Title);
        // onChoose?.(c);
        setStopSearchById(true);
        onChoose?.(c);
        return;
      }
    }

    if (defaultValue) {
      if (!cases) return;
      //@ts-ignore
      const c = cases?.Result?.Items?.find((Case) => Case.Id === defaultValue);
      if (c) {
        setValue(c.Title);
        onChoose?.(c);
      }
    }

    if (isCase && !disableSettingActiveCase) {
      const packs = window.location.href.split("/");
      const index = packs.findIndex((t) => t === "cases");
      const caseId = packs.at(index + 1);
      setSearchedCaseId(caseId!);
    }

    //@ts-ignore
    setSearchList(cases?.Result?.Items);
  }, [cases, tRef]);

  useEffect(() => {
    if (settedCase) {
      setValue(`${settedCase.Title}`);
      forceUpdate();
      return;
    }
  }, [settedCase]);

  useEffect(() => {
    refetch();
  }, [debouncedValue]);

  const handleClick = (id: string) => {
    const cas = searchList.find((c) => c.Id === id);
    setValue(`${cas.Title}`);
    setActive(false);
    onChoose?.(cas);
  };

  const handleCreateNewCase = () => {
    if (!parent.length) {
      setModals({ ...modals, createCase: true, hide: false });
      return;
    }
    setChildren("createCase");
  };

  return (
    <div ref={ref} className={`${styles.wrapper} ${className}`}>
      <div className={styles.focus}>
        <Input
          // focus={removeFocus ? false : true}
          onFocus={() => setActive(true)}
          // onBlur={() => setActive(false)}
          value={value}
          onRightClick={allowCreateNew ? handleCreateNewCase : null}
          onRightText={t("modals.newCase")}
          isError={isError}
          onChange={(text) => {
            if (text === "") {
              onChoose?.(null);
            }
            setValue(text);
          }}
          label={t("modals.case")}
          labelPosition={labelPosition}
          variant={variant}
          disableBorder={disableBorder}
        />
      </div>
      {active && (
        <DropdownInput
          toggle={() => setActive(false)}
          items={searchList?.map((c) => ({
            id: c.Id,
            title: c.Title,
            sub:
              c.Signature && c.Client
                ? `${c.Signature} | ${c.Client}`
                : c.Client ?? c.Signature,
          }))}
          onClick={(id) => handleClick(id)}
          isLoading={isLoading}
          subClass={styles.caseSignature}
        />
      )}
    </div>
  );
};

export default SearchCasesNew;
